import type { FC, KeyboardEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getBonusWord, getTierName } from './account.helpers';
import styles from './BonusesPanel.module.scss';

type UserCard = {
    balance?: number | string;
    discount?: number | string;
    level?: string;
};
type BonusesUser = {
    firstName?: string;
    lastName?: string;
    card?: UserCard;
};
type Props = {
    user: BonusesUser;
};
type CardTheme = 'base' | 'silver' | 'gold' | 'platinum';

const normalizeTier = (raw: unknown): CardTheme => {
    const v = String(raw ?? '')
        .trim()
        .toLowerCase();
    if (v === 'platinum' || v === 'vip') return 'platinum';
    if (v === 'gold') return 'gold';
    if (v === 'silver') return 'silver';
    if (v === 'base') return 'base';
    return 'base';
};
const toNumberLoose = (v: unknown): number | null => {
    if (v == null) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const s = String(v).trim();
    if (!s) return null;
    const cleaned = s.replace(/\s+/g, '').replace(/%/g, '').replace(',', '.');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
};
const toIntOrZero = (v: unknown): number => {
    const n = toNumberLoose(v);
    if (n == null) return 0;
    return Math.trunc(n);
};

const BonusesPanel: FC<Props> = ({ user }) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const flipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (flipTimerRef.current) clearTimeout(flipTimerRef.current);
        };
    }, []);

    const balanceValue = useMemo(() => toIntOrZero(user?.card?.balance), [user?.card?.balance]);
    const discountValue = useMemo(() => toNumberLoose(user?.card?.discount), [user?.card?.discount]);
    const tierRaw = useMemo(() => String(user?.card?.level ?? '').trim(), [user?.card?.level]);
    const tier = useMemo<CardTheme>(() => normalizeTier(tierRaw), [tierRaw]);
    const tierName = useMemo(() => getTierName(tier) || '-', [tier]);

    const fullName = useMemo(() => {
        const firstName = String(user?.firstName ?? '').trim();
        const lastName = String(user?.lastName ?? '').trim();
        const parts = [lastName, firstName].filter(Boolean);
        const name = parts.join(' ').trim();
        return name || firstName || lastName || 'Покупатель';
    }, [user?.firstName, user?.lastName]);

    const discountText = useMemo(() => {
        if (discountValue == null) return '-';
        const rounded = Math.round(discountValue * 100) / 100;
        return String(rounded);
    }, [discountValue]);

    const handleCardClick = () => {
        if (isFlipped) {
            setIsFlipped(false);
            if (flipTimerRef.current) clearTimeout(flipTimerRef.current);
            return;
        }
        setIsFlipped(true);
        if (flipTimerRef.current) clearTimeout(flipTimerRef.current);
        flipTimerRef.current = setTimeout(() => {
            setIsFlipped(false);
        }, 10_000);
    };

    const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
        }
    };

    const cardClassName = useMemo(() => {
        const themeClass =
            tier === 'platinum'
                ? styles.loyaltyCard_themePlatinum
                : tier === 'gold'
                  ? styles.loyaltyCard_themeGold
                  : tier === 'silver'
                    ? styles.loyaltyCard_themeSilver
                    : styles.loyaltyCard_themeBase;
        return [styles.loyaltyCard, themeClass, isFlipped ? styles.loyaltyCard_flipped : ''].filter(Boolean).join(' ');
    }, [tier, isFlipped]);

    return (
        <div className={styles.wrapper}>
            <h2 className={styles.title}>Бонусы</h2>
            {/* ===== Бонусная карта ===== */}
            <div className={styles.loyaltyWrap}>
                <div
                    className={cardClassName}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isFlipped}
                    aria-label="Бонусная карта. Нажмите, чтобы перевернуть."
                    onClick={handleCardClick}
                    onKeyDown={handleCardKeyDown}
                >
                    <div className={styles.loyaltyInner}>
                        {/* Лицевая сторона */}
                        <div className={[styles.loyaltySide, styles.loyaltyFront].join(' ')}>
                            <div className={styles.loyaltyTop}>
                                <img src="/logo.png" alt="С.Габай" className={styles.loyaltyLogo} draggable={false} />
                                <div className={styles.loyaltyBadge}>
                                    <span className={styles.loyaltyBadgeLabel}>Уровень</span>
                                    <span className={styles.loyaltyBadgeValue}>{tierName}</span>
                                </div>
                            </div>
                            <div className={styles.loyaltyMid}>
                                <div className={styles.loyaltyName}>{fullName}</div>
                                <div className={styles.loyaltyHint}>Нажмите, чтобы увидеть баланс и скидку</div>
                            </div>
                            <a className={styles.loyaltySupport} href="tel:+74952806055" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                Служба поддержки +7 (495) 280-60-55
                            </a>
                        </div>
                        {/* Обратная сторона */}
                        <div className={[styles.loyaltySide, styles.loyaltyBack].join(' ')}>
                            <div className={styles.loyaltyBackTop}>
                                <div className={styles.loyaltyBrand}>С.Габай</div>
                                <div className={styles.loyaltyDiscountPill}>
                                    <span className={styles.loyaltyDiscount}>
                                        {discountText}
                                        {discountText === '-' ? '' : '%'}
                                    </span>
                                    <span className={styles.loyaltyDiscountText}>скидка</span>
                                </div>
                            </div>
                            <div className={styles.loyaltyBackMid}>
                                <div className={styles.loyaltyBalance}>
                                    {balanceValue} {getBonusWord(balanceValue)}
                                </div>
                                <div className={styles.loyaltyMeta}>
                                    {discountText}
                                    {discountText === '-' ? '' : '%'} - {tierName} уровень
                                </div>
                            </div>
                            <a className={styles.loyaltySupport} href="tel:+74952806055" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                Служба поддержки +7 (495) 280-60-55
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* ===== Сводка ===== */}
            <div className={styles.card}>
                <div className={styles.row}>
                    <span className={styles.label}>Бонусный баланс</span>
                    <span className={styles.value}>
                        {balanceValue} {getBonusWord(balanceValue)}
                    </span>
                </div>
                <div className={styles.row}>
                    <span className={styles.label}>Кэшбэк по карте</span>
                    <span className={styles.value}>
                        {discountText}
                        {discountText === '-' ? '' : '%'}
                    </span>
                </div>
                <div className={styles.row}>
                    <span className={styles.label}>Уровень</span>
                    <span className={styles.value}>{tierName}</span>
                </div>
            </div>
        </div>
    );
};

export default BonusesPanel;
